import { useEffect, useCallback, useState } from 'react';

// ==========================================
// TYPES
// ==========================================

type OrientationType = 'portrait' | 'landscape' | 'any';

interface OrientationLockResult {
  /** Current orientation lock state */
  isLocked: boolean;
  /** Whether device supports orientation lock */
  isSupported: boolean;
  /** Current device orientation */
  currentOrientation: 'portrait' | 'landscape';
  /** Lock to specific orientation */
  lock: (orientation: OrientationType) => Promise<void>;
  /** Unlock orientation */
  unlock: () => Promise<void>;
}

// Extended Screen Orientation interface for browsers that support lock()
// Using 'any' for types since not all browsers have full TypeScript support
interface ExtendedScreenOrientation {
  lock?: (lockType: string) => Promise<void>;
  unlock?: () => void;
  type?: string;
  angle?: number;
}

// ==========================================
// HOOK
// ==========================================

/**
 * Hook to manage screen orientation lock on mobile devices.
 * On desktop, this is a no-op.
 *
 * Usage:
 * - Lock to portrait for most app views
 * - Unlock (allow both portrait/landscape) for game view
 */
export function useOrientationLock(
  defaultOrientation: OrientationType = 'any'
): OrientationLockResult {
  const [isLocked, setIsLocked] = useState(false);
  const [currentOrientation, setCurrentOrientation] = useState<'portrait' | 'landscape'>('portrait');

  // Check if Screen Orientation API lock is supported
  const getOrientation = (): ExtendedScreenOrientation | null => {
    if (typeof window === 'undefined' || !('screen' in window) || !('orientation' in screen)) {
      return null;
    }
    return screen.orientation as ExtendedScreenOrientation;
  };

  const isSupported = (() => {
    const orientation = getOrientation();
    return orientation !== null && typeof orientation.lock === 'function';
  })();

  // Detect current orientation
  useEffect(() => {
    const updateOrientation = () => {
      if (typeof window !== 'undefined') {
        const isPortrait = window.innerHeight > window.innerWidth;
        setCurrentOrientation(isPortrait ? 'portrait' : 'landscape');
      }
    };

    updateOrientation();

    window.addEventListener('resize', updateOrientation);
    window.addEventListener('orientationchange', updateOrientation);

    return () => {
      window.removeEventListener('resize', updateOrientation);
      window.removeEventListener('orientationchange', updateOrientation);
    };
  }, []);

  // Lock to specific orientation
  const lock = useCallback(async (orientation: OrientationType) => {
    const screenOrientation = getOrientation();

    if (!screenOrientation || typeof screenOrientation.lock !== 'function') {
      console.log('[OrientationLock] Not supported on this device');
      return;
    }

    try {
      if (orientation === 'any') {
        screenOrientation.unlock?.();
        setIsLocked(false);
        console.log('[OrientationLock] Unlocked');
      } else if (orientation === 'portrait') {
        await screenOrientation.lock('portrait-primary');
        setIsLocked(true);
        console.log('[OrientationLock] Locked to portrait');
      } else {
        await screenOrientation.lock('landscape-primary');
        setIsLocked(true);
        console.log('[OrientationLock] Locked to landscape');
      }
    } catch (error) {
      // Common reasons for failure:
      // - Not in fullscreen mode (some browsers require this)
      // - Feature policy prevents it
      // - Device doesn't support it
      console.warn('[OrientationLock] Failed to lock orientation:', error);
    }
  }, []);

  // Unlock orientation
  const unlock = useCallback(async () => {
    const screenOrientation = getOrientation();

    if (!screenOrientation || typeof screenOrientation.unlock !== 'function') {
      return;
    }

    try {
      screenOrientation.unlock();
      setIsLocked(false);
      console.log('[OrientationLock] Unlocked');
    } catch (error) {
      console.warn('[OrientationLock] Failed to unlock:', error);
    }
  }, []);

  // Apply default orientation on mount
  useEffect(() => {
    if (defaultOrientation !== 'any') {
      lock(defaultOrientation);
    }

    return () => {
      // Clean up by unlocking on unmount
      const screenOrientation = getOrientation();
      if (screenOrientation && typeof screenOrientation.unlock === 'function') {
        try {
          screenOrientation.unlock();
        } catch {
          // Ignore cleanup errors
        }
      }
    };
  }, [defaultOrientation, lock]);

  return {
    isLocked,
    isSupported,
    currentOrientation,
    lock,
    unlock,
  };
}

// ==========================================
// UTILITIES
// ==========================================

/**
 * Check if running on mobile device
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Check if device is in portrait mode
 */
export function isPortrait(): boolean {
  if (typeof window === 'undefined') return true;
  return window.innerHeight > window.innerWidth;
}

/**
 * Check if device is in landscape mode
 */
export function isLandscape(): boolean {
  return !isPortrait();
}
